'use client'

import { useCallback, useEffect, useState } from 'react'
import { motion } from 'motion/react'
import { Skeleton } from '@material-bank/mb3-components'
import { discoveryTileImageSource } from '@material-bank/mtag-utilities'
import { CirclingElements } from '../CirclingElements/CirclingElements'
import { BlurRevealPhrases } from '../BlurRevealPhrases/BlurRevealPhrases'
import type { InterstitialCardImage } from './buildInterstitialImagePool'
import { useSequenceStart } from '../../hooks/useSequenceStart'
import {
  ENTRANCE_EASING,
  INTERSTITIAL_CARD_COUNT,
  INTERSTITIAL_REVEAL_COUNT,
  interstitialTiming,
  type InterstitialTiming,
} from '../../motion'

// Only the exit path animates this container (it mounts already at opacity 1),
// so this is tuned to the fast dismissal, not a leisurely reveal.
const EXIT_FADE_DURATION = 0.2
const EASING_CUSTOM_OUT = [0.165, 0.84, 0.44, 1] as const
const CARD_KEYS = Array.from(
  { length: INTERSTITIAL_CARD_COUNT },
  (_, i) => `card-${String(i)}`,
)
// A card's image settles into place from slightly zoomed and out of focus, so the
// orbit reads as the search bringing results into view rather than a pop-in.
const IMAGE_HIDDEN = { opacity: 0, scale: 1.15, filter: 'blur(8px)' }
const IMAGE_SHOWN = { opacity: 1, scale: 1, filter: 'blur(0px)' }

// How many images have had their turn so far. The orbit fills one image at a
// time, like a loading sequence — every card once, then the results that take
// over the product cards (buildInterstitialImagePool) — paced by `timing` so the
// last image has settled as the overlay's floor ends. Counted from `startedAt`,
// so a sequence that began before hydration catches up rather than restarting.
function useRevealCount(
  { firstRevealMs, revealIntervalMs }: InterstitialTiming,
  startedAt: number,
): number {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const elapsed = Date.now() - startedAt
    const timers = Array.from({ length: INTERSTITIAL_REVEAL_COUNT }, (_, i) =>
      setTimeout(
        () => setCount(i + 1),
        Math.max(0, firstRevealMs + i * revealIntervalMs - elapsed),
      ),
    )

    return () => {
      for (const timer of timers) {
        clearTimeout(timer)
      }
    }
  }, [firstRevealMs, revealIntervalMs, startedAt])

  return count
}

type CardImageProps = {
  url: string
  revealed: boolean
  revealMs: number
}

function CardImage({ url, revealed, revealMs }: CardImageProps) {
  const [loaded, setLoaded] = useState(false)
  // The image mounts with the card so its bytes load while earlier steps play; a
  // cached (or server-rendered, already-loaded) image never fires onLoad, so the
  // ref settles it from `complete`.
  const imageRef = useCallback((el: HTMLImageElement | null) => {
    if (el?.complete && el.naturalWidth > 0) {
      setLoaded(true)
    }
  }, [])
  const visible = revealed && loaded

  return (
    <motion.img
      {...discoveryTileImageSource(url)}
      alt=""
      animate={visible ? IMAGE_SHOWN : IMAGE_HIDDEN}
      className="absolute inset-0 size-full object-cover"
      initial={IMAGE_HIDDEN}
      onLoad={() => setLoaded(true)}
      ref={imageRef}
      transition={{
        duration: revealMs / 1000,
        ease: ENTRANCE_EASING,
      }}
    />
  )
}

type SearchInterstitialProps = {
  // Per orbit card, the images it shows in order (see buildInterstitialImagePool).
  cards: InterstitialCardImage[][]
  isExiting: boolean
  onPhrasesComplete: () => void
  phrases: string[]
  // Pace of the loading sequence; the same timing useSearchInterstitial floors on.
  timing?: InterstitialTiming
}

export function SearchInterstitial({
  cards,
  isExiting,
  onPhrasesComplete,
  phrases,
  timing = interstitialTiming(),
}: SearchInterstitialProps) {
  const { startedAt, fromServerHtml } = useSequenceStart()
  const revealCount = useRevealCount(timing, startedAt)

  return (
    <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-gray-100">
      <CirclingElements
        duration={30}
        isExiting={isExiting}
        radius={196}
        skipEntrance={fromServerHtml}
      >
        {CARD_KEYS.map((key, i) => (
          <div
            className="relative size-20 overflow-hidden rounded-xl shadow-lg"
            key={key}
          >
            <Skeleton className="absolute inset-0 size-full" />
            {(cards[i] ?? []).map((image, imageIndex) => (
              <CardImage
                // Keyed by URL: the pool swaps fallbacks for results once they
                // arrive, and a new image must load (and reveal) on its own.
                key={`${String(imageIndex)}-${image.url}`}
                revealed={image.order < revealCount}
                revealMs={timing.revealMs}
                url={image.url}
              />
            ))}
          </div>
        ))}
      </CirclingElements>

      <motion.div
        animate={{ opacity: isExiting ? 0 : 1 }}
        className="absolute z-10 flex items-center justify-center"
        transition={{
          duration: EXIT_FADE_DURATION,
          ease: EASING_CUSTOM_OUT,
        }}
      >
        <BlurRevealPhrases
          className="text-gray-500 tracking-tight"
          intervalMs={timing.stepMs}
          onComplete={onPhrasesComplete}
          phrases={phrases}
          skipEntrance={fromServerHtml}
          startedAt={startedAt}
        />
      </motion.div>
    </div>
  )
}
